<template>
  <div class="space-y-6">
    <h1 class="page-title">Documentações</h1>

    <div class="card">
      <div class="card-header">
        <div class="docs-layout">
          <div class="docs-tree">
            <div class="docs-search">
              <div class="search-wrapper">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  placeholder="Pesquisar documentos..."
                />
                <span class="search-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M10 2a8 8 0 105.293 14.293l4.207 4.207 1.414-1.414-4.207-4.207A8 8 0 0010 2zm0 2a6 6 0 110 12A6 6 0 0110 4z"/>
                  </svg>
                </span>
              </div>
            </div>
            <TreeView 
              v-if="(filteredTree && filteredTree.children) || (tree && tree.children)" 
              :nodes="(filteredTree && filteredTree.children) ? filteredTree.children : (tree?.children || [])" 
              :expand-all="!!searchQuery"
              @select="openFile"
            />
            <div v-else class="empty">Nenhum documento encontrado.</div>
          </div>
          <div class="docs-content" :style="markdown ? containerStyle : {}">
            <!-- Overlay de Controles de Zoom -->
            <div v-if="markdown" class="zoom-overlay">
              <div class="zoom-buttons">
                <button 
                  @click="zoomOut" 
                  :disabled="zoomLevel <= minZoom"
                  class="zoom-btn"
                  title="Diminuir zoom"
                >
                  <svg fill="currentColor" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M19 13H5v-2h14v2z"/>
                  </svg>
                </button>
                
                <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
                
                <button 
                  @click="zoomIn" 
                  :disabled="zoomLevel >= maxZoom"
                  class="zoom-btn"
                  title="Aumentar zoom"
                >
                  <svg fill="currentColor" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                </button>
              </div>
            </div>

            <div v-if="markdown" class="docs-header">
              <div v-if="currentName" class="docs-title">{{ currentName }}</div>
              <div v-if="currentPath" class="docs-path">{{ currentPath }}</div>
            </div>
            <div class="docs-body">
              <div v-if="loading" class="loading">Carregando...</div>
              <div v-else-if="markdown" class="markdown-zoom-wrapper" :style="zoomStyle">
                <vue-markdown :source="markdown" class="markdown-body" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import VueMarkdown from 'vue-markdown-render'
import 'github-markdown-css/github-markdown-light.css'
import TreeView from './TreeView.vue'

type Node = {
  name: string
  path: string
  type: 'dir' | 'file'
  children?: Node[]
}

const tree = ref<Node | null>(null)
const searchQuery = ref('')
const markdown = ref('')
const currentPath = ref('')
const currentName = ref('')
const loading = ref(false)

// Estados do zoom
const zoomLevel = ref(1)
const minZoom = 0.5
const maxZoom = 2
const zoomStep = 0.1

onMounted(async () => {
  try {
    const res = await fetch('/docs/manifest.json', { cache: 'no-store' })
    if (res.ok) {
      tree.value = await res.json()
    }
  } catch {
    console.warn('Manifesto ausente. Execute: npm run docs:manifest')
  }
})

const openFile = async (node: Node) => {
  if (node.type !== 'file') return
  loading.value = true
  markdown.value = ''
  try {
    // buscar conteúdo bruto
    const res = await fetch(`/docs${node.path}`, { cache: 'no-store' })
    const text = await res.text()
    markdown.value = text
    currentPath.value = 'Path: ' + formatDisplayPath(`docs${node.path}`)
    currentName.value = stripExt(decodeSafe(node.name))
  } catch {
    markdown.value = '*Falha ao carregar arquivo.*'
    currentPath.value = formatDisplayPath(`docs${node.path}`)
    currentName.value = stripExt(decodeSafe(node.name))
  } finally {
    loading.value = false
  }
}

// Filtragem de árvore por nome ou caminho (case-insensitive)
const filteredTree = computed<Node | null>(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q || !tree.value) return null

  const filterNode = (n: Node): Node | null => {
    const selfMatch = n.name.toLowerCase().includes(q) || n.path.toLowerCase().includes(q)
    if (n.type === 'file') {
      return selfMatch ? { ...n } : null
    }
    const kids = (n.children || [])
      .map(filterNode)
      .filter((c): c is Node => c !== null)
    if (selfMatch || kids.length) {
      return { ...n, children: kids }
    }
    return null
  }

  return filterNode(tree.value)
})

function decodeSafe(s: string) {
  try { return decodeURIComponent(s) } catch { return s }
}

function stripExt(name: string) {
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(0, i) : name
}

function formatDisplayPath(full: string) {
  // remove prefixo 'docs/' e decodifica componentes; remove extensão do último segmento
  const p = full.replace(/^\/?docs\/?/, '')
  const parts = p.split('/')
  const decoded = parts.map(decodeSafe)
  if (decoded.length) {
    decoded[decoded.length - 1] = stripExt(decoded[decoded.length - 1])
  }
  return decoded.join('/')
}

// Funções de controle de zoom
const zoomIn = () => {
  if (zoomLevel.value < maxZoom) {
    zoomLevel.value = Math.min(maxZoom, zoomLevel.value + zoomStep)
  }
}

const zoomOut = () => {
  if (zoomLevel.value > minZoom) {
    zoomLevel.value = Math.max(minZoom, zoomLevel.value - zoomStep)
  }
}

// Computed para o estilo de zoom com scroll dinâmico
const zoomStyle = computed(() => ({
  transform: `scale(${zoomLevel.value})`,
  transformOrigin: 'top left',
  width: `${100 / zoomLevel.value}%`,
  height: zoomLevel.value < 1 ? `${100 / zoomLevel.value}%` : 'auto'
}))

// Computed para o estilo do container com scroll ajustado
const containerStyle = computed(() => ({
  overflow: 'auto',
  maxHeight: `${67.8 / Math.max(zoomLevel.value, 0.8)}vh` // Altura base ajustada pelo zoom
}))

</script>

<style scoped>
.docs-container { max-width: 1000px; margin: 0 auto; padding: 1rem; }
.sub { color: #6b7280; margin-bottom: 1rem; }
.empty { color: #9ca3af; font-style: italic; }

.docs-layout {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.docs-tree {
  flex: 0 0 20%;
}

.docs-content {
  flex: 1 1 auto;
  height: 67.8vh;
  border: 1px solid var(--lwsa-gray-200);
  border-radius: 0.75rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  min-width: 0; /* evita overflow lateral em layout flex */
}

.md {
  padding: 1rem;
  line-height: 1.6;
  word-break: break-word;
}

.loading, .placeholder { padding: 1rem; }

.docs-header { 
  flex: 0 0 auto; 
  border-bottom: 2px solid var(--lwsa-gray-200);
}
.docs-body { 
  flex: 1 1 auto;
  overflow: auto; 
  min-height: 0; 
  min-width: 0; /* permite que o conteúdo contraia e a barra apareça */
  padding-top: 1.2rem;
}

/* Ajustes para evitar overflow lateral do tema GitHub */
:deep(.markdown-body) {
  max-width: 100%;
  width: 100%;
  padding: 0; /* já temos padding no container */
  box-sizing: border-box;
}

/* Evitar overflow em blocos de código largos */
:deep(.markdown-body pre) {
  overflow: auto;
}

/* Quebra de palavras longas */
:deep(.markdown-body), :deep(.markdown-body p) {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.docs-title {
  font-size: 1.5rem;
  font-weight: 600;
}
.docs-path {
  font-size: 0.7rem;
  color: var(--lwsa-gray-600);
  padding-bottom: 1rem;
}

/* Busca */
.docs-search { padding: 0rem 0rem 0.75rem 0rem; }
.search-wrapper { position: relative; }
.search-input {
  width: 100%;
  border: 1px solid var(--lwsa-gray-200);
  border-radius: 0.5rem;
  padding: 0.5rem 2rem 0.5rem 0.75rem; /* espaço para a lupa à direita */
  font-size: 0.9rem;
}
.search-icon {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--lwsa-gray-500);
  pointer-events: none;
}
.search-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
  border-color: #2563eb;
}

/* Overlay de Controles de Zoom */
.zoom-overlay {
  position: sticky;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  margin-left: auto;
  width: fit-content;
  margin-bottom: -3rem; /* Compensa a altura para não afetar o layout */
}

.zoom-overlay:hover {
  opacity: 1;
}

.zoom-buttons {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 1.25rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--lwsa-gray-200);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(6px);
}

.zoom-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: var(--lwsa-gray-600);
  transition: all 0.2s ease;
}

.zoom-btn:hover:not(:disabled) {
  background: var(--lwsa-gray-100);
  color: var(--lwsa-gray-800);
  transform: scale(1.05);
}

.zoom-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-level {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--lwsa-gray-700);
  min-width: 40px;
  text-align: center;
  padding: 0 0.375rem;
  user-select: none;
}

/* Wrapper de Zoom */
.markdown-zoom-wrapper {
  transition: transform 0.2s ease;
  transform-origin: top left;
  min-height: 100%;
  display: block;
}

/* Ajustes para o conteúdo markdown no Docs.vue */
.docs-content {
  position: relative;
  transition: max-height 0.2s ease;
}

/* Otimizações para diferentes níveis de zoom */
.docs-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.docs-content::-webkit-scrollbar-track {
  background: var(--lwsa-gray-100);
  border-radius: 4px;
}

.docs-content::-webkit-scrollbar-thumb {
  background: var(--lwsa-gray-300);
  border-radius: 4px;
}

.docs-content::-webkit-scrollbar-thumb:hover {
  background: var(--lwsa-gray-400);
}

</style>


